// packages/ui/src/components/identity-verification/index.jsx
// 공통 본인 인증 컴포넌트 (Phase 6 dedup — bssm 877줄 표준 superset)
// - branchId 를 prop 으로 받아 브랜치별 캐시 키를 파생한다.
// - Thai(th) 문구 포함(구 gangnam/guro/hongdae 전용 → 전 지점 확장).
// - clearIdentityVerificationCache 내보내기 포함(구 guro 전용).
// - 카카오 버튼 "준비 중..." 로딩 라벨 포함(구 uijeongbu 전용).
"use client";
import { integrationPolicy } from "@kkeut/lib/config/integrationPolicy";

import { useEffect, useMemo, useRef, useState } from "react";
import { LogOut } from "lucide-react";

/** ───────── 인증 글로벌 캐시 (1시간 TTL) ───────── */
const GLOBAL_IV_TTL_MS = 60 * 60 * 1000; // 1h

/** 인증 상태가 바뀌었음을 같은 탭에 알린다 (#1114).
 *
 * 헤더의 인증 해제 버튼이 이 신호로 나타나고 사라진다. `storage` 이벤트는
 * **다른 탭에만** 간다 — 같은 탭에서 인증을 끝내면 헤더가 안 바뀐다. */
export const IV_CHANGED_EVENT = "kkeut:iv-changed";

function notifyIvChanged() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(IV_CHANGED_EVENT));
  } catch { }
}

function saveExternalIV(key, info) {
  if (typeof window === "undefined") return;
  try {
    const payload = { v: info, ts: Date.now() };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch { }
  notifyIvChanged();
}

/** 이 지점에 살아 있는 인증이 있나 — 헤더가 버튼을 띄울지 정할 때 쓴다 (#1114).
 *
 * 서버에 묻지 않는다. `/api/verify/session` GET 은 접근 쿠키를 갱신하고 지점
 * 훅을 부르는 **부수효과가 있어서**, 페이지마다 부르면 세션이 끝나지 않는다.
 * 저장값이 서버보다 오래 남아 있더라도 해제를 누르면 양쪽이 같이 지워지므로
 * 틀린 쪽으로 기울지 않는다. */
export function hasLocalIdentity(branchId) {
  if (typeof window === "undefined") return false;
  return !!loadExternalIV(`iv:${branchId}:global`);
}

function loadExternalIV(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (Date.now() - (parsed.ts || 0) > GLOBAL_IV_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.v || null;
  } catch {
    return null;
  }
}

/**
 * 로그아웃/캐시 초기화 유틸 (구 guro 전용 → 공통화).
 * branchId 를 첫 인자로 받는다(브랜치별 IV 키를 사용하기 때문).
 */
export function clearIdentityVerificationCache(branchId, locale = "ko") {
  if (typeof window === "undefined") return;
  const GLOBAL_IV_KEY = `iv:${branchId}:global`;
  try {
    const lang = String(locale || "ko").toLowerCase().split("-")[0];
    localStorage.removeItem(GLOBAL_IV_KEY);
    sessionStorage.removeItem(`iv:${branchId}:${lang}`);
    // 예약 페이지가 쓰는 캐시. 한쪽만 지우면 인증 상태가 되살아난다.
    localStorage.removeItem("kkeut_iv_v1");
  } catch {}
  notifyIvChanged();
  // 서버 세션도 함께 끊는다. 남겨 두면 새로고침 시 인증이 복원돼
  // "로그아웃했는데 그대로" 가 된다. 동기 함수라 결과는 기다리지 않는다.
  try {
    fetch("/api/verify/session", { method: "DELETE", keepalive: true }).catch(() => {});
  } catch {}
}

/**
 * 인증을 끝낸다 — 화면 저장값을 지우고 서버 세션 삭제(DELETE)가 끝날 때까지 기다린다(2026-10-14).
 * 기다리지 않으면 곧바로 다시 뜬 인증 화면의 세션 조회(GET)가 삭제보다 먼저 처리돼 방금 끝낸 세션을
 * 되살린다 → 조회가 또 거절되고 같은 흐름이 반복된다(코덱스 10-14). 응답이 늦어도 3초 뒤에는 넘어간다.
 */
export async function endIdentitySession(branchId, locale = "ko") {
  if (typeof window === "undefined") return;
  try {
    const lang = String(locale || "ko").toLowerCase().split("-")[0];
    localStorage.removeItem(`iv:${branchId}:global`);
    sessionStorage.removeItem(`iv:${branchId}:${lang}`);
    localStorage.removeItem("kkeut_iv_v1");
  } catch {}
  notifyIvChanged();
  try {
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 3000);
    try {
      await fetch("/api/verify/session", { method: "DELETE", signal: ac.signal });
    } finally {
      clearTimeout(timer);
    }
  } catch {}
}

/** ───────── 국가번호(한국만 필요) ───────── */
const COUNTRIES = [
  { code: "+82", label: { ko: "대한민국 +82", en: "Korea +82", ja: "韓国 +82", zh: "韩国 +82", th: "เกาหลี +82" } },
];

/** ───────── 텍스트 ───────── */
const TXT = {
  title: { ko: "본인 인증", en: "Identity verification", ja: "本人確認", zh: "本人认证", th: "ยืนยันตัวตน" },
  desc: {
    ko: "예약 진행을 위해 본인 인증을 진행합니다.",
    en: "We verify your identity to proceed with the reservation.",
    ja: "予約手続きのため、本人確認を行います。",
    zh: "为继续预约，我们需要进行身份认证。",
    th: "เพื่อดำเนินการจอง กรุณายืนยันตัวตน",
  },
  done: { ko: "인증 완료", en: "Verified", ja: "認証済み", zh: "已认证", th: "ยืนยันสำเร็จ" },

  // Kakao
  kakaoTitle: { ko: "카카오톡 인증", en: "Kakao Verification", ja: "Kakao認証", zh: "Kakao认证", th: "ยืนยันด้วย Kakao" },
  kakaoDesc: {
    ko: "카카오 간편인증으로 빠르게 인증할 수 있어요.",
    en: "Fast verification with Kakao simple auth.",
    ja: "Kakaoの簡単認証ですぐに確認できます。",
    zh: "通过Kakao便捷认证快速完成。",
    th: "ยืนยันตัวตนได้อย่างรวดเร็วด้วย Kakao",
  },
  kakaoBtn: { ko: "카카오 인증하기", en: "Verify with Kakao", ja: "Kakaoで認証", zh: "使用Kakao认证", th: "ยืนยันด้วย Kakao" },

  // SMS (ko 전용)
  smsTitle: { ko: "문자(SMS) 인증", en: "SMS Verification", ja: "SMS本人確認", zh: "短信认证", th: "ยืนยันด้วย SMS" },
  smsDesc: {
    ko: "휴대폰 번호로 인증코드를 받아 인증하세요.",
    en: "Receive a code via SMS to verify.",
    ja: "携帯番号にコードを送り認証します。",
    zh: "通过短信接收验证码进行认证。",
    th: "รับรหัสยืนยันผ่าน SMS เพื่อยืนยันตัวตน",
  },
  smsStart: { ko: "문자 인증하기", en: "Start SMS", ja: "SMSで認証", zh: "开始短信认证", th: "เริ่มยืนยันด้วย SMS" },

  // Email (ko 외)
  emailTitle: { ko: "이메일 인증", en: "Email Verification", ja: "e-mail 本人確認", zh: "邮箱认证", th: "ยืนยันด้วยอีเมล" },
  emailDesc: {
    ko: "이메일로 인증코드를 받아 인증하세요.",
    en: "Receive a code by email to verify.",
    ja: "メールでコードを受け取り認証します。",
    zh: "通过邮箱接收验证码进行认证。",
    th: "รับรหัสยืนยันทางอีเมลเพื่อยืนยันตัวตน",
  },
  emailStart: { ko: "이메일 인증하기", en: "Start Email", ja: "メール認証を開始", zh: "开始邮箱认证", th: "เริ่มยืนยันด้วยอีเมล" },

  // 공통 폼
  name: { ko: "이름", en: "Name", ja: "お名前", zh: "姓名", th: "ชื่อ" },
  passportHint: {
    ko: "여권 표기명과 동일하게 적어주세요.",
    en: "Please write your name as on your passport.",
    ja: "パスポートのローマ字と同じお名前をご記入ください。",
    zh: "请填写与护照一致的英文姓名。",
    th: "กรุณากรอกชื่อให้ตรงกับหนังสือเดินทาง",
  },
  getCode: { ko: "인증코드 받기", en: "Get code", ja: "認証コードを送信", zh: "获取验证码", th: "ขอรับรหัสยืนยัน" },
  code: { ko: "인증코드", en: "Verification code", ja: "認証コード", zh: "验证码", th: "รหัสยืนยัน" },
  verify: { ko: "인증", en: "Verify", ja: "認証", zh: "认证", th: "ยืนยัน" },
  emailPh: { ko: "예) name@example.com", en: "e.g. name@example.com", ja: "例) name@example.com", zh: "例）name@example.com", th: "เช่น name@example.com" },
  change: { ko: "변경", en: "Change", ja: "変更", zh: "更改", th: "เปลี่ยน" },
};
const label = (o, lang) => o?.[lang] ?? o?.en ?? "";

/** ───────── 인증 해제 버튼 (#1114) ─────────
 *
 * 한 번 인증하면 쿠키가 만료될 때까지(1시간) 그 신원이 화면에 고정됐다. 매장
 * 태블릿이나 가족 공용 기기에서 앞사람 인증이 남거나, 번호를 잘못 인증했을 때
 * 사용자가 스스로 풀 방법이 없었다.
 *
 * 이 홈페이지에는 회원가입·로그인이 늘 보이는 구조가 없다. 인증은 예약·조회를
 * 위한 1회성이라 "로그아웃" 이 아니라 **"인증 해제"** 로 부른다.
 *
 * ⚠️ 화면 저장값만 지우면 안 된다. 서버 세션이 남으면 새로고침할 때 인증이
 * 복원돼 "해제했는데 그대로" 가 된다. endIdentitySession 이 그 순서를 지키므로
 * (저장값 삭제 → DELETE 완료까지 대기) 여기서 쿠키를 직접 지우지 않는다.
 */
const TXT_END = {
  end: { ko: "인증 해제", en: "Sign out", ja: "認証解除", zh: "解除认证", th: "ออกจากระบบ" },
  ending: { ko: "해제 중…", en: "Signing out…", ja: "解除中…", zh: "解除中…", th: "กำลังออก…" },
};

export function EndIdentityButton({
  /* 안 넘기면 빌드에 박힌 지점 id 를 쓴다 — 앱이 지점마다 따로 빌드되므로 그 값이
   * 맞다. packages/ui 의 다른 컴포넌트(kiosk)도 같은 방식이다. prop 이 20개가 넘는
   * 화면에 하나 더 늘리지 않기 위해서다. */
  branchId = (process.env.NEXT_PUBLIC_BRANCH_ID || "").trim(),
  locale = "ko",
  onEnded,
  className = "",
  /* "text" — 화면 안쪽(인증 완료 배너·예약 배지·시술권). 무슨 일이 일어나는지
   *          글로 보이는 편이 낫다.
   * "icon" — 헤더. 장바구니·메뉴와 나란히 서므로 아이콘이 줄을 맞춘다. */
  variant = "text",
  size = 20,
}) {
  const [busy, setBusy] = useState(false);
  const lang = String(locale || "ko").toLowerCase().split("-")[0];
  const text = label(busy ? TXT_END.ending : TXT_END.end, lang);

  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      /* 서버 세션 삭제가 끝난 뒤에 화면을 되돌린다. 먼저 되돌리면 다시 뜬
       * 인증 화면의 세션 조회(GET)가 삭제보다 먼저 처리돼 방금 끝낸 세션을
       * 되살린다 — onAuthExpired 가 같은 이유로 await 한다. */
      await endIdentitySession(branchId, lang);
      onEnded?.();
    } finally {
      setBusy(false);
    }
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={run}
        /* 아이콘만 있는 버튼이다. 라벨이 없으면 화면낭독기 사용자에게는
         * "버튼" 으로만 읽혀 무엇인지 알 수 없다. */
        aria-label={text}
        title={text}
        className={"p-1 transition-opacity hover:opacity-70 disabled:opacity-50 " + className}
      >
        <LogOut size={size} suppressHydrationWarning />
      </button>
    );
  }

  return (
    <button
      type="button"
      disabled={busy}
      onClick={run}
      className={
        "text-xs text-gray-500 underline underline-offset-2 disabled:opacity-50 " + className
      }
    >
      {text}
    </button>
  );
}

/** ───────── 유틸 ───────── */
function normalizeE164(code, rawDigits) {
  let d = (rawDigits || "").replace(/\D/g, "");
  return `${code}${d.replace(/^0+/, "")}`;
}
function pickDefaultMode({ hasKakao, hasSms, hasEmail, defaultMethod }) {
  const allowed = [];
  if (hasKakao) allowed.push("kakao");
  if (hasEmail) allowed.push("email");
  if (hasSms) allowed.push("sms");
  if (defaultMethod && allowed.includes(defaultMethod)) return defaultMethod;
  return allowed[0] || "email";
}

export default function IdentityVerification(props) {
  // 미정 TODO — 카카오 심사 전 지점은 인증 상태를 조회하거나 UI를 렌더하지 않는다.
  if (integrationPolicy()?.identityDisabled) return null;
  return <IdentityVerificationContent {...props} />;
}

function IdentityVerificationContent({
  branchId,
  locale = "en",
  channels = ["email"], // 기본: 이메일
  defaultCountry = "+82",
  onVerified,
  onCleared,
  defaultMethod,
  resetOnUnmount = true, // 예약페이지에서는 보통 false 권장(이탈해도 유지)
  // 예약 단계 통계(#1104) — ("start"|"success"|"restore"|"error", { method, error }). 넘긴 지점만 받는다.
  onFunnel,
}) {
  // 인증 수단은 언어와 무관하게 지점 설정(channels)만 따른다.
  // 예전에는 카카오·SMS 를 한국어로만 제한했는데, 이메일이 없는 지점(15곳)에서는
  // 외국어로 들어오면 인증 수단이 하나도 없어 예약 자체가 막혔다. 카카오·SMS UI
  // 모두 en/ja/zh/th 번역을 갖추고 있어 제한할 이유가 없다.
  const hasKakao = channels.includes("kakao");
  const hasSms = channels.includes("sms");
  const hasEmail = channels.includes("email");

  // 부모 콜백 참조를 ref로 고정 (무한 루프 방지)
  const onVerifiedRef = useRef(onVerified);
  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);
  const onFunnelRef = useRef(onFunnel);
  onFunnelRef.current = onFunnel;
  const funnel = (type, info) => {
    try {
      onFunnelRef.current?.(type, info);
    } catch {}
  };

  const GLOBAL_IV_KEY = `iv:${branchId}:global`;
  const STORAGE_KEY = `iv:${branchId}:${locale}`;

  const resolvedDefault = useMemo(
    () => pickDefaultMode({ hasKakao, hasSms, hasEmail, defaultMethod }),
    [hasKakao, hasSms, hasEmail, defaultMethod]
  );

  // 카드 → 폼 전환 토글
  const [smsOpen, setSmsOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  /** 초기 스냅샷 */
  const initial = useMemo(() => {
    const fallback = {
      snapshot: { name: "", email: "", phoneDigits: "", country: defaultCountry },
      lockAfterSend: false,
      token: null,
      mode: resolvedDefault,
    };
    if (typeof window === "undefined") return fallback;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return fallback;
      const saved = JSON.parse(raw);
      const savedMode = typeof saved.mode === "string" ? saved.mode : null;
      const modeOk =
        (savedMode === "kakao" && hasKakao) ||
        (savedMode === "email" && hasEmail) ||
        (savedMode === "sms" && hasSms);
      return {
        snapshot: {
          name: saved.snapshot?.name || "",
          email: saved.snapshot?.email || "",
          phoneDigits: saved.snapshot?.phoneDigits || "",
          country: saved.snapshot?.country || defaultCountry,
        },
        lockAfterSend: !!saved.lockAfterSend,
        token: saved.token || null,
        mode: modeOk ? savedMode : resolvedDefault,
      };
    } catch {
      return fallback;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 상태
  const [mode, setMode] = useState(initial.mode);
  const [token, setToken] = useState(initial.token);
  const [ok, setOk] = useState(false);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [verifiedMethod, setVerifiedMethod] = useState(null);
  // 서버가 인증 세션을 발급할 수 있는지: "unknown" | "on" | "off".
  // 확인 전(unknown)에 클릭하면 인앱 사용자가 잘못된 경로로 빠지므로
  // 그동안은 카카오 버튼을 잠근다(수백 ms 다).
  const [sessionProbe, setSessionProbe] = useState("unknown");
  // onVerified 중복 호출 방지. 로컬 복원과 서버 세션 복원이 겹칠 수 있다.
  const reportedRef = useRef(false);
  const [lockAfterSend, setLockAfterSend] = useState(initial.lockAfterSend);

  // refs
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const codeRef = useRef(null);
  const countryRef = useRef(null);

  const snapshotRef = useRef(initial.snapshot);
  const countryDefault = snapshotRef.current.country || defaultCountry;

  useEffect(() => {
    // 모드 전환 시, 폼 내 "OK" 라벨만 초기화 (성공 배너는 frozen 기준이라 영향 없음)
    setOk(false);
  }, [mode]);

  // 모드 교정 (허용 채널 변화 시만)
  useEffect(() => {
    if (frozen || lockAfterSend) return;
    setMode((curr) => {
      const currOk =
        (curr === "kakao" && hasKakao) ||
        (curr === "email" && hasEmail) ||
        (curr === "sms" && hasSms);
      return currOk ? curr : resolvedDefault;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDefault, hasKakao, hasSms, hasEmail]);

  // 유령 잠금 해제 (토큰이 사라졌는데 lock이 켜져 있으면 해제)
  useEffect(() => {
    if (!frozen && lockAfterSend && !token) {
      setLockAfterSend(false);
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const saved = raw ? JSON.parse(raw) : {};
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...saved, lockAfterSend: false })
        );
      } catch { }
    }
  }, [frozen, lockAfterSend, token, STORAGE_KEY]);

  // 언마운트 시 초기화 (예약페이지에서는 resetOnUnmount={false} 권장)
  useEffect(() => {
    if (!resetOnUnmount) return;
    return () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch { }
      onCleared?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 값 helpers
  const getName = () => (nameRef.current?.value || "").trim();
  const getEmail = () => (emailRef.current?.value || "").trim();
  const getDigits = () => (phoneRef.current?.value || "").replace(/\D/g, "");
  const getCountry = () => countryRef.current?.value || countryDefault;
  const getPhoneE164 = () => normalizeE164(getCountry(), getDigits());
  const getCode = () => (codeRef.current?.value || "").trim();

  const persistNow = (overrides = {}) => {
    const payload = {
      snapshot: snapshotRef.current,
      lockAfterSend,
      token,
      mode,
      ...overrides,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch { }
  };

  const onNameChange = () => {
    snapshotRef.current = { ...snapshotRef.current, name: getName() };
    persistNow();
  };
  const onEmailChange = () => {
    snapshotRef.current = { ...snapshotRef.current, email: getEmail() };
    persistNow();
  };
  const onPhoneChange = () => {
    snapshotRef.current = { ...snapshotRef.current, phoneDigits: getDigits() };
    persistNow();
  };
  const onCountryChange = () => {
    snapshotRef.current = { ...snapshotRef.current, country: getCountry() };
    persistNow();
  };

  /** Kakao popup */
  useEffect(() => {
    const origin = typeof window === "undefined" ? "*" : window.location.origin;
    const onMessage = (e) => {
      if (e.origin !== origin) return;
      const { type, payload } = e.data || {};
      if (type === "KAKAO_VERIFIED") {
        setOk(true);
        setFrozen(true);
        setVerifiedMethod("kakao");
        setMode("kakao");

        if (!snapshotRef.current.name && (payload?.name || payload?.nickname)) {
          snapshotRef.current = {
            ...snapshotRef.current,
            name: payload.name || payload.nickname,
          };
          if (nameRef.current && !nameRef.current.value)
            nameRef.current.value = snapshotRef.current.name;
        }

        const verified = {
          method: "kakao",
          name: snapshotRef.current.name || "",
          phoneE164: payload?.phoneE164 || null,
          kakaoProfile: payload || null,
        };

        // 1시간 글로벌 유지
        saveExternalIV(GLOBAL_IV_KEY, verified);
        // 부모 전달 (ref 통해 무한 루프 방지)
        onVerifiedRef.current?.(verified);
        funnel("success", { method: "kakao" });

        persistNow({ lockAfterSend: true, token: null, mode: "kakao" });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // 의존성 없음: 한 번만 등록
  }, []);

  // 서버 인증 세션 복원.
  // 카카오톡 인앱 웹뷰에는 팝업이 없어 postMessage 가 오지 않는다. 같은 탭으로
  // 다녀온 뒤 여기서 결과를 되찾는다.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 응답이 안 오면 버튼이 영영 잠긴다. 반드시 끊어 준다.
        const ac = new AbortController();
        const timer = setTimeout(() => ac.abort(), 5000);
        let r;
        try {
          r = await fetch("/api/verify/session", { cache: "no-store", signal: ac.signal });
        } finally {
          clearTimeout(timer);
        }
        const data = await r.json();
        if (!alive) return;

        // 서명 키가 없으면 같은 탭으로 나가면 안 된다(결과를 받을 방법이 없다).
        // 오류 응답(401/500 등)이나 필드 누락은 "세션 기능 없음"의 증거가 아니다.
        // 그런 응답을 "off" 로 읽으면 팝업으로 후퇴해 인앱 예약이 막힌다.
        if (!r.ok) throw new Error("verify/session " + r.status);
        setSessionProbe(data?.sessionEnabled === false ? "off" : "on");

        const v = data?.verified;
        if (!v) return;
        // 이미 부모에게 알렸으면 중복 호출하지 않는다.
        if (reportedRef.current) return;

        setOk(true);
        setFrozen(true);
        setVerifiedMethod(v.method || "kakao");

        if (v.name && nameRef.current && !nameRef.current.value)
          nameRef.current.value = v.name;
        if (v.email && emailRef.current && !emailRef.current.value)
          emailRef.current.value = v.email;

        const verifiedInfo = {
          method: v.method || "kakao",
          name: v.name || "",
          phoneE164: v.phoneE164 || null,
          birthDate: v.birthDate || null,
        };
        saveExternalIV(GLOBAL_IV_KEY, verifiedInfo);
        reportedRef.current = true;
        onVerifiedRef.current?.(verifiedInfo);
        // 카카오 인증을 마치고 돌아온 것인지, 1시간 안의 재방문인지는 받는 쪽이 가린다.
        funnel("restore", { method: verifiedInfo.method });
      } catch {
        // 세션 조회 실패는 치명적이지 않다 — 사용자가 다시 인증하면 된다.
        // 조회 실패는 "세션 기능이 없다"는 증거가 아니다. 서버가 명시적으로
        // sessionEnabled:false 를 준 경우에만 "off" 여야 한다.
        // 인앱에서 팝업은 확정 실패지만 같은 탭은 서버가 회복하면 성공한다.
        setSessionProbe((p) => (p === "unknown" ? "on" : p));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 카카오 인증이 실패하면 주소에 ?verify=사유(expired·cancelled·failed)를 붙여 돌아온다(identity/server failPage).
  // 부모(예약 화면)가 시도를 연 뒤에 알리도록 한 박자 미룬다 — 자식 effect 가 부모보다 먼저 돈다.
  useEffect(() => {
    if (!onFunnelRef.current) return;
    const reason = new URLSearchParams(window.location.search).get("verify");
    if (!reason) return;
    const t = setTimeout(() => funnel("error", { method: "kakao", error: reason }));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const startKakao = () => {
    if (frozen && verifiedMethod === "kakao") return;

    // 예전에는 window.open 으로 팝업을 띄우고 postMessage 로만 결과를 받았다.
    // 카카오톡 인앱 웹뷰는 팝업을 만들지 않아 콜백에서 window.opener 가 null 이고,
    // 그때 인증 결과가 통째로 버려졌다(화면에는 "새로고침"으로 보였다).
    // 팝업 여부에 기대지 않도록 같은 탭으로 이동한다. 결과는 서버가 쿠키에 담아
    // 두므로, 돌아온 뒤 아래 세션 복원 effect 가 되찾는다.

    // 아직 확인 중이면 아무것도 하지 않는다(버튼도 잠겨 있다).
    if (sessionProbe === "unknown") return;

    // returnTo 는 어느 경로로 가든 반드시 넘긴다. 빠뜨리면 서버 기본값
    // "/main/reserve" 로 돌아가서 다른 페이지에서 시작한 사용자가 흐름을 잃는다.
    const returnTo = `${window.location.pathname}${window.location.search}`;
    const url = `/api/verify/kakao/start?returnTo=${encodeURIComponent(returnTo)}`;
    funnel("start", { method: "kakao" });

    // 서버가 세션을 발급할 수 없으면(서명 키 미설정) 같은 탭으로 나가면 안 된다.
    // 결과를 받을 방법이 없어 인증이 아예 불가능해진다. 그때는 예전 팝업 방식으로 둔다.
    if (sessionProbe === "off") {
      const w = 480;
      const h = 700;
      const y = (window.outerHeight || window.innerHeight) / 2 + (window.screenY || 0) - h / 2;
      const x = (window.outerWidth || window.innerWidth) / 2 + (window.screenX || 0) - w / 2;
      window.open(url, "kakaoAuth", `width=${w},height=${h},left=${x},top=${y}`);
      return;
    }

    window.location.assign(url);
  };

  /** 코드 전송 (SMS/Email 공용) */
  async function sendCode() {
    if (frozen || lockAfterSend) return;

    snapshotRef.current = {
      name: getName(),
      email: getEmail(),
      phoneDigits: getDigits(),
      country: getCountry(),
    };
    persistNow();

    let via = "email";
    let to = "";
    if (mode === "sms") {
      const digits = getDigits();
      if (digits.length < 9) return alert("전화번호를 정확히 입력해주세요.");
      via = "sms";
      to = getPhoneE164();
    } else {
      const em = getEmail();
      if (!/\S+@\S+\.\S+/.test(em)) return alert("올바른 이메일을 입력해주세요.");
      via = "email";
      to = em;
    }

    funnel("start", { method: via });
    setSending(true);
    try {
      const res = await fetch("/api/verify/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ via, to, locale, branchId }),
      }).then((r) => r.json());

      if (res.ok) {
        setToken(res.token);
        setLockAfterSend(true);
        setOk(false);
        persistNow({ token: res.token, lockAfterSend: true });
        if (
          process.env.NODE_ENV !== "production" &&
          res.devCode &&
          codeRef.current
        ) {
          codeRef.current.value = res.devCode; // 개발 편의
        }
        alert(
          label(
            {
              ko: "인증코드를 전송했어요.",
              en: "Verification code sent.",
              ja: "認証コードを送信しました。",
              zh: "验证码已发送。",
            },
            locale
          )
        );
      } else {
        funnel("error", { method: via, error: res.error });
        alert(`Failed: ${res.error || "send_failed"}`);
      }
    } catch (e) {
      funnel("error", { method: via, error: "network" });
      throw e;
    } finally {
      setSending(false);
    }
  }

  /** 코드 검증 */
  async function verify() {
    if (frozen) return;
    const code = getCode();
    if (!token || !code) return;

    setChecking(true);
    try {
      const res = await fetch("/api/verify/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, code, branchId }),
      }).then((r) => r.json());

      if (res.ok) {
        setOk(true);
        setFrozen(true);
        const name = snapshotRef.current.name || getName() || "";
        let verified;
        if (mode === "sms") {
          setVerifiedMethod("sms");
          verified = { method: "sms", name, phoneE164: getPhoneE164() };
        } else {
          setVerifiedMethod("email");
          verified = {
            method: "email",
            name,
            email: snapshotRef.current.email || getEmail() || "",
          };
        }
        // 1시간 글로벌 유지
        saveExternalIV(GLOBAL_IV_KEY, verified);
        // 부모 전달 (ref 통해 무한 루프 방지)
        onVerifiedRef.current?.(verified);
        funnel("success", { method: verified.method });

        persistNow({ token: null, lockAfterSend: true });
      } else {
        funnel("error", { method: mode === "sms" ? "sms" : "email", error: res.error });
        alert("Invalid or expired code.");
      }
    } catch (e) {
      funnel("error", { method: mode === "sms" ? "sms" : "email", error: "network" });
      throw e;
    } finally {
      setChecking(false);
    }
  }

  // 외부(글로벌) 인증 복원: 최초 1회만
  useEffect(() => {
    const ext = loadExternalIV(GLOBAL_IV_KEY);
    if (!ext) return;
    setOk(true);
    setFrozen(true);
    const m =
      ext.method || (ext.kakaoProfile ? "kakao" : ext.email ? "email" : "sms");
    setVerifiedMethod(m);
    // 허용된 모드면 반영
    if (
      (m === "kakao" && hasKakao) ||
      (m === "email" && hasEmail) ||
      (m === "sms" && hasSms)
    ) {
      setMode(m);
    }
    if (ext.name && nameRef.current && !nameRef.current.value)
      nameRef.current.value = ext.name;
    if (ext.email && emailRef.current && !emailRef.current.value)
      emailRef.current.value = ext.email;

    // 부모에게도 알려줌(최초 마운트 시 1회)
    onVerifiedRef.current?.(ext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lockedInputs = frozen || lockAfterSend;
  const verified = !!frozen;

  /* ---------- View Blocks ---------- */
  const SuccessBanner = () => (
    <div className="rounded-xl border bg-white p-4 text-center">
      <p className="text-sm font-semibold" style={{ color: "#16a34a" }}>
        {label(TXT.done, locale)}
      </p>
      {/* 인증 해제 (#1114). 이 배너는 인증을 유지한 채 머무는 화면들이 공유한다
          — 여기 두면 예약 조회·변경 화면이 한 번에 덮인다.
          해제 뒤에는 이 컴포넌트의 상태도 되돌려 인증 수단 카드가 다시 뜨게
          한다. 서버 세션만 지우고 화면을 두면 "해제했는데 그대로" 가 된다. */}
      <div className="mt-2">
        <EndIdentityButton
          branchId={branchId}
          locale={locale}
          onEnded={() => {
            setFrozen(false);
            setOk(false);
            setToken(null);
            setLockAfterSend(false);
            onCleared?.();
          }}
        />
      </div>
    </div>
  );

  const KakaoCompact = () => (
    <div className="h-full w-full max-w-[480px] rounded-2xl border bg-white p-6 sm:p-8 text-center">
      <button
        type="button"
        onClick={startKakao}
        // 세션 발급 가능 여부를 확인하기 전에는 잠근다.
        // 모르는 상태로 누르면 인앱 사용자가 엉뚱한 페이지로 끌려간다.
        disabled={(frozen && verifiedMethod === "kakao") || sessionProbe === "unknown"}
        aria-disabled={(frozen && verifiedMethod === "kakao") || sessionProbe === "unknown"}
        className={`w-full min-h-[220px] rounded-2xl border px-6 py-8 transition focus:outline-none flex items-center justify-center ${
          frozen && verifiedMethod === "kakao"
            ? "opacity-60 cursor-not-allowed pointer-events-none"
            : sessionProbe === "unknown"
            ? "opacity-60 cursor-wait"
            : "hover:shadow-lg"
        }`}
      >
        <div className="mx-auto flex flex-col items-center gap-4">
          <div
            className="flex h-20 w-20 items-center justify-center rounded-full"
            style={{ backgroundColor: "#FEE500" }}
          >
            <svg width="38" height="38" viewBox="0 0 48 48" aria-hidden="true">
              <path
                fill="#3B1E1E"
                d="M24 10c-8.284 0-15 5.037-15 11.25 0 3.914 2.536 7.341 6.47 9.387L14 36l5.18-3.612c1.561.258 3.21.395 4.82.395 8.284 0 15-5.037 15-11.25S32.284 10 24 10z"
              />
            </svg>
          </div>
          <div className="text-lg font-extrabold" style={{ color: "#b9a792" }}>
            {sessionProbe === "unknown"
              ? (locale === "ko" ? "준비 중..." : "Loading...")
              : label(TXT.kakaoBtn, locale)}
          </div>
        </div>
      </button>
    </div>
  );

  // 한국어: SMS 카드/폼
  const SmsCompactOrForm = () => {
    if (!smsOpen) {
      return (
        <div className="h-full w-full max-w-[480px] rounded-2xl border bg-white p-6 sm:p-8 text-center">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border">
            <svg width="32" height="32" viewBox="0 0 24 24">
              <path
                d="M6 2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H8l-4 4V4a2 2 0 0 1 2-2z"
                fill="currentColor"
              />
            </svg>
          </div>
          <p className="text-lg font-extrabold">{label(TXT.smsTitle, locale)}</p>
          <p className="mt-2 text-sm text-gray-500">{label(TXT.smsDesc, locale)}</p>
          <button
            type="button"
            onClick={() => {
              setSmsOpen(true);
              setMode("sms");
            }}
            className="mt-5 w-full rounded-2xl border px-6 py-4 hover:shadow-lg transition"
          >
            {label(TXT.smsStart, locale)}
          </button>
        </div>
      );
    }
    return (
      <div className="w-full max-w-[480px] rounded-xl border p-4 space-y-3">
        {/* 이름 */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label className="text-sm">{label(TXT.name, locale)}</label>
            {lockAfterSend && !frozen && (
              <button
                type="button"
                className="text-xs underline"
                onClick={() => {
                  setLockAfterSend(false);
                  setToken(null);
                  setOk(false);
                  persistNow({ lockAfterSend: false, token: null });
                }}
              >
                {label(TXT.change, locale)}
              </button>
            )}
          </div>
          <input
            ref={nameRef}
            className="border rounded-md px-3 py-2"
            placeholder={label(TXT.name, locale)}
            defaultValue={snapshotRef.current.name}
            readOnly={lockedInputs}
            onChange={onNameChange}
          />
        </div>

        {/* 국가/전화/코드 */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <div className="w-full sm:w-36">
            <select
              ref={countryRef}
              className="w-full border rounded-md px-3 py-2"
              defaultValue={countryDefault}
              disabled={lockedInputs}
              onChange={onCountryChange}
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {label(c.label, locale)}
                </option>
              ))}
            </select>
          </div>
          <input
            ref={phoneRef}
            className="w-full flex-1 border rounded-md px-3 py-2"
            placeholder="010-..."
            inputMode="tel"
            defaultValue={snapshotRef.current.phoneDigits}
            readOnly={lockedInputs}
            onChange={onPhoneChange}
          />
          <button
            type="button"
            onClick={sendCode}
            disabled={sending || lockedInputs}
            className="w-full sm:w-auto rounded-md border px-3 py-2 whitespace-nowrap"
          >
            {sending ? "..." : label(TXT.getCode, locale)}
          </button>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            ref={codeRef}
            className="w-full flex-1 border rounded-md px-3 py-2"
            placeholder={label(TXT.code, locale)}
            inputMode="numeric"
            readOnly={frozen}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={verify}
              disabled={checking || !token || frozen}
              className="w-full sm:w-auto rounded-md border px-3 py-2 whitespace-nowrap"
            >
              {checking ? "..." : label(TXT.verify, locale)}
            </button>
            <span
              className="self-center text-sm"
              style={{ color: ok ? "#16a34a" : "transparent" }}
            >
              {label(TXT.done, locale)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ko 외: Email 카드/폼
  const EmailCompactOrForm = () => {
    if (!emailOpen) {
      return (
        <div className="h-full w-full max-w-[480px] rounded-2xl border bg-white p-6 sm:p-8 text-center mx-auto">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full border">
            <svg width="32" height="32" viewBox="0 0 24 24">
              <path d="M4 6h16v12H4z" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M4 6l8 6 8-6" fill="none" stroke="currentColor" strokeWidth="2" />
            </svg>
          </div>
          <p className="text-lg font-extrabold">{label(TXT.emailTitle, locale)}</p>
          <p className="mt-2 text-sm text-gray-500">{label(TXT.emailDesc, locale)}</p>
          <button
            type="button"
            onClick={() => {
              setEmailOpen(true);
              setMode("email");
            }}
            className="mt-5 w-full rounded-2xl border px-6 py-4 hover:shadow-lg transition"
          >
            {label(TXT.emailStart, locale)}
          </button>
        </div>
      );
    }
    return (
      <div className="w-full max-w-[480px] rounded-xl border p-4 space-y-3 mx-auto">
        {/* 이름 */}
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <label className="text-sm">{label(TXT.name, locale)}</label>
            {lockAfterSend && !frozen && (
              <button
                type="button"
                className="text-xs underline"
                onClick={() => {
                  setLockAfterSend(false);
                  setToken(null);
                  setOk(false);
                  persistNow({ lockAfterSend: false, token: null });
                }}
              >
                {label(TXT.change, locale)}
              </button>
            )}
          </div>
          <input
            ref={nameRef}
            className="border rounded-md px-3 py-2"
            placeholder={label(TXT.name, locale)}
            defaultValue={snapshotRef.current.name}
            readOnly={lockedInputs}
            onChange={onNameChange}
          />
          {locale !== "ko" && (
            <p className="text-xs text-gray-500">{label(TXT.passportHint, locale)}</p>
          )}
        </div>

        {/* 이메일/코드 */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
          <input
            ref={emailRef}
            className="w-full flex-1 border rounded-md px-3 py-2"
            placeholder={label(TXT.emailPh, locale)}
            inputMode="email"
            defaultValue={snapshotRef.current.email}
            readOnly={lockedInputs}
            onChange={onEmailChange}
          />
          <button
            type="button"
            onClick={sendCode}
            disabled={sending || lockedInputs}
            className="w-full sm:w-auto rounded-md border px-3 py-2 whitespace-nowrap"
          >
            {sending ? "..." : label(TXT.getCode, locale)}
          </button>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            ref={codeRef}
            className="w-full flex-1 border rounded-md px-3 py-2"
            placeholder={label(TXT.code, locale)}
            inputMode="numeric"
            readOnly={frozen}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={verify}
              disabled={checking || !token || frozen}
              className="w-full sm:w-auto rounded-md border px-3 py-2 whitespace-nowrap"
            >
              {checking ? "..." : label(TXT.verify, locale)}
            </button>
            <span
              className="self-center text-sm"
              style={{ color: ok ? "#16a34a" : "transparent" }}
            >
              {label(TXT.done, locale)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ▶ 카드 개수 계산: 1개면 1열(가운데), 2개 이상이면 2열
  const cardCount = (hasKakao ? 1 : 0) + (hasSms ? 1 : 0) + (hasEmail ? 1 : 0);

  return (
    <div className="mx-auto max-w-[900px] space-y-2">
      <p className="text-lg font-semibold">{label(TXT.title, locale)}</p>
      <p className="text-sm text-gray-600">{label(TXT.desc, locale)}</p>

      {verified ? (
        <SuccessBanner />
      ) : (
        <>
          {/* 언어와 무관하게 지점 설정(channels)대로 카드를 띄운다.
              예전에는 비한국어일 때 이메일 카드만 그렸는데, 이메일이 없는
              지점에서는 인증 수단이 하나도 안 보여 예약이 막혔다. */}
          <div
            className={[
              "mt-2 grid gap-4 sm:gap-6",
              "grid-cols-1",
              cardCount >= 2 ? "sm:grid-cols-2" : "",
              cardCount >= 2 ? "justify-items-stretch" : "justify-items-center",
              "place-content-center",
              "w-full",
            ].join(" ")}
          >
            {hasKakao && <KakaoCompact />}
            {hasEmail && <EmailCompactOrForm />}
            {hasSms && <SmsCompactOrForm />}
          </div>
        </>
      )}
    </div>
  );
}
